import { PracticeTemplate } from '@cibo/core'
import { usePermissions } from '@cibo/profile'
import AddIcon from '@mui/icons-material/Add'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

interface TemplateEditorSidebarProps {
  templates: PracticeTemplate[]
  isLoading: boolean
  selectedId: string | 'new'
  onSelect: (id: string | 'new') => void
  draftName?: string
  onDraftNameChange?: (name: string) => void
  nameError?: string | null
  canEdit?: boolean
  onSave?: () => void
  isAdvisorOrAdmin?: boolean
  farmerUserId?: number
  authUserId?: number
}

export const TemplateEditorSidebar = ({
  templates,
  isLoading,
  selectedId,
  onSelect,
  isAdvisorOrAdmin,
  farmerUserId,
  authUserId,
}: TemplateEditorSidebarProps) => {
  const { isDebugUser } = usePermissions()
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')

  const myTemplates = isAdvisorOrAdmin
    ? templates.filter(tmpl => tmpl.owner === authUserId)
    : templates

  const farmerTemplates = farmerUserId ? templates.filter(tmpl => tmpl.owner === farmerUserId) : []

  const orphanedTemplates =
    isAdvisorOrAdmin && isDebugUser
      ? templates.filter(tmpl => tmpl.owner !== authUserId && tmpl.owner !== farmerUserId)
      : []

  return (
    <Box
      sx={{
        width: 260,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1 }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{t('sidebarTitle')}</Typography>
        <Tooltip title={t('newTemplate')}>
          <IconButton size="small" onClick={() => onSelect('new')} aria-label={t('newTemplate')}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <List dense sx={{ overflow: 'auto', flex: 1 }}>
          {selectedId === 'new' && (
            <ListItem
              disablePadding={false}
              data-testid="template-sidebar-new-placeholder"
              sx={{ px: 2, py: 0.5 }}
            >
              <ListItemText
                primary={t('newTemplate')}
                slotProps={{
                  primary: { sx: { fontStyle: 'italic', opacity: 0.6 } },
                }}
              />
            </ListItem>
          )}

          {isAdvisorOrAdmin ? (
            <>
              {farmerUserId && (
                <>
                  <ListSubheader
                    disableSticky
                    data-testid="template-sidebar-section-thisFarmer"
                    sx={{ lineHeight: '32px' }}
                  >
                    {t('thisFarmerSection')}
                  </ListSubheader>
                  {farmerTemplates.length === 0 && (
                    <Box sx={{ px: 3, pb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('noTemplatesYet')}
                      </Typography>
                    </Box>
                  )}
                  {farmerTemplates.map(tmpl => (
                    <TemplateItem
                      key={tmpl.id}
                      template={tmpl}
                      selectedId={selectedId}
                      onSelect={onSelect}
                      indented
                    />
                  ))}
                </>
              )}

              <ListSubheader
                disableSticky
                data-testid="template-sidebar-section-anyFarmer"
                sx={{ lineHeight: '32px' }}
              >
                {t('anyFarmerSection')}
              </ListSubheader>
              {myTemplates.length === 0 && (
                <Box sx={{ px: 3, pb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('noTemplatesYet')}
                  </Typography>
                </Box>
              )}
              {myTemplates.map(tmpl => (
                <TemplateItem
                  key={tmpl.id}
                  template={tmpl}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  indented
                />
              ))}

              {orphanedTemplates.length > 0 && (
                <>
                  <ListSubheader
                    disableSticky
                    data-testid="template-sidebar-section-otherUsers"
                    sx={{
                      lineHeight: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: theme => alpha(theme.palette.error.main, 0.2),
                    }}
                  >
                    <BugReportOutlinedIcon fontSize="small" color="error" />
                    {t('otherUsersSection')}
                  </ListSubheader>
                  {orphanedTemplates.map(tmpl => (
                    <TemplateItem
                      key={tmpl.id}
                      template={tmpl}
                      selectedId={selectedId}
                      onSelect={onSelect}
                      errorBackground
                    />
                  ))}
                </>
              )}
            </>
          ) : (
            <>
              {templates.length === 0 && selectedId !== 'new' && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('noTemplatesYet')}
                  </Typography>
                </Box>
              )}
              {templates.map(tmpl => (
                <TemplateItem
                  key={tmpl.id}
                  template={tmpl}
                  selectedId={selectedId}
                  onSelect={onSelect}
                />
              ))}
            </>
          )}
        </List>
      )}
    </Box>
  )
}

const TemplateItem = ({
  template,
  errorBackground = false,
  indented = false,
  selectedId,
  onSelect,
}: {
  template: PracticeTemplate
  errorBackground?: boolean
  indented?: boolean
  selectedId: string | 'new'
  onSelect: (id: string) => void
}) => (
  <ListItemButton
    key={template.id}
    data-testid={`template-sidebar-item-${template.id}`}
    selected={selectedId === template.id}
    onClick={() => onSelect(template.id)}
    sx={{
      pl: indented ? 3 : 2,
      ...(errorBackground ? { bgcolor: theme => alpha(theme.palette.error.main, 0.2) } : {}),
    }}
  >
    <ListItemText
      primary={template.name}
      slotProps={{
        primary: {
          noWrap: true,
          sx: { textOverflow: 'ellipsis' },
        },
      }}
    />
  </ListItemButton>
)
